import { useState, useEffect } from 'react';
import { apiGet } from '../services/api';
import { formatTimetrackCurrency } from '../utils/time';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  Clock,
  DollarSign,
  TrendingUp,
  Hash,
  Calendar,
} from 'lucide-react';

interface Summary {
  total_hours: number;
  billable_hours: number;
  total_amount: number;
  entry_count: number;
}

interface ProjectReport {
  project_id: string | null;
  project_name: string;
  project_color: string | null;
  client_name: string | null;
  total_hours: number;
  billable_hours: number;
  total_amount: number;
  entry_count: number;
}

interface ClientReport {
  client_id: string | null;
  client_name: string;
  total_hours: number;
  billable_hours: number;
  total_amount: number;
  entry_count: number;
}

const CHART_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#14b8a6', '#f59e0b', '#ef4444', '#3b82f6'];

type DatePreset = 'today' | 'week' | 'month' | 'last7' | 'last30';

function getDateRange(preset: DatePreset): { start: string; end: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today': {
      const iso = today.toISOString();
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      return { start: iso, end: endOfDay.toISOString() };
    }
    case 'week': {
      const day = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      return { start: monday.toISOString(), end: sunday.toISOString() };
    }
    case 'month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      last.setHours(23, 59, 59, 999);
      return { start: first.toISOString(), end: last.toISOString() };
    }
    case 'last7': {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      const end = new Date(today);
      end.setHours(23, 59, 59, 999);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    case 'last30': {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      const end = new Date(today);
      end.setHours(23, 59, 59, 999);
      return { start: start.toISOString(), end: end.toISOString() };
    }
  }
}

export default function TimeTrackReports() {
  const [preset, setPreset] = useState<DatePreset>('month');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [byProject, setByProject] = useState<ProjectReport[]>([]);
  const [byClient, setByClient] = useState<ClientReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, [preset]);

  const fetchReports = async () => {
    setIsLoading(true);
    const { start, end } = getDateRange(preset);
    const params = `start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`;

    try {
      const [summaryData, projectData, clientData] = await Promise.all([
        apiGet<Summary>(`/api/reports/timetrack/summary?${params}`),
        apiGet<ProjectReport[]>(`/api/reports/timetrack/by-project?${params}`),
        apiGet<ClientReport[]>(`/api/reports/timetrack/by-client?${params}`),
      ]);
      setSummary(summaryData);
      setByProject(projectData);
      setByClient(clientData);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const presets: { key: DatePreset; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'last7', label: 'Last 7 Days' },
    { key: 'last30', label: 'Last 30 Days' },
  ];

  const barData = byProject.map((p) => ({
    name: p.project_name.length > 15 ? p.project_name.slice(0, 15) + '...' : p.project_name,
    hours: Number(p.total_hours),
    billable: Number(p.billable_hours),
  }));

  const pieData = byClient
    .filter((c) => Number(c.total_amount) > 0)
    .map((c) => ({
      name: c.client_name,
      value: Number(c.total_amount),
    }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">TimeTrack Reports</h1>
          <p className="text-gray-400">Analytics and insights for your time tracking</p>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar className="w-4 h-4 text-gray-500" />
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              preset === p.key
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading reports...</div>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-gray-400">Total Time</span>
                </div>
                <p className="text-2xl font-bold text-white">{Number(summary.total_hours).toFixed(1)}h</p>
              </div>
              <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-gray-400">Billable Time</span>
                </div>
                <p className="text-2xl font-bold text-green-400">{Number(summary.billable_hours).toFixed(1)}h</p>
              </div>
              <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-gray-400">Total Earnings</span>
                </div>
                <p className="text-2xl font-bold text-yellow-400">{formatTimetrackCurrency(Number(summary.total_amount))}</p>
              </div>
              <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-gray-400">Entries</span>
                </div>
                <p className="text-2xl font-bold text-white">{summary.entry_count}</p>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Bar Chart: Time by Project */}
            <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Time by Project</h3>
              {barData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">No data for this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      axisLine={{ stroke: '#374151' }}
                    />
                    <YAxis
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      axisLine={{ stroke: '#374151' }}
                      label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem',
                        color: '#fff',
                      }}
                    />
                    <Bar dataKey="hours" name="Total Hours" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="billable" name="Billable Hours" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Pie Chart: Earnings by Client */}
            <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Earnings by Client</h3>
              {pieData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">No billable data for this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }: any) => `${name || ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#9ca3af' }}
                    >
                      {pieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem',
                        color: '#fff',
                      }}
                      formatter={(value: unknown) => formatTimetrackCurrency(Number(value))}
                    />
                    <Legend
                      wrapperStyle={{ color: '#9ca3af' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Project Breakdown Table */}
          <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-800">
              <h3 className="text-lg font-semibold text-white">Project Breakdown</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-800">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Project</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Client</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Total Hours</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Billable</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Earnings</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {byProject.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No data for this period</td>
                    </tr>
                  ) : (
                    byProject.map((p, i) => (
                      <tr key={p.project_id || i} className="border-b border-dark-800/50 hover:bg-dark-800/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: p.project_color || '#6366f1' }}
                            />
                            <span className="text-sm text-white">{p.project_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">{p.client_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-white font-mono">{Number(p.total_hours).toFixed(1)}h</td>
                        <td className="px-4 py-3 text-sm text-right text-green-400 font-mono">{Number(p.billable_hours).toFixed(1)}h</td>
                        <td className="px-4 py-3 text-sm text-right text-yellow-400">{formatTimetrackCurrency(Number(p.total_amount))}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-400">{p.entry_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
